import dayjs from 'dayjs'

export const TASK_DATETIME_FORMAT = 'YYYY-MM-DD HH:mm:ss'
export const BEGIN_DEFAULT_TIME = '00:00:00'
export const EXPECTED_DEFAULT_TIME = '23:59:59'

export type TaskDateField = 'begin' | 'expected'

export type ParsedTaskDateTime = {
  date: string | null
  time: string | null
  hasCustomTime: boolean
}

const DATE_ONLY_REGEX = /^\d{4}-\d{2}-\d{2}$/
const DATETIME_REGEX = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}(:\d{2})?$/

export function parseTaskDateTime(value?: string | null): ParsedTaskDateTime {
  if (!value || value.trim() === '') {
    return {date: null, time: null, hasCustomTime: false}
  }

  const normalized = value.trim()

  if (DATE_ONLY_REGEX.test(normalized)) {
    return {date: normalized, time: null, hasCustomTime: false}
  }

  const parsed = dayjs(normalized)
  if (!parsed.isValid()) {
    return {date: null, time: null, hasCustomTime: false}
  }

  const date = parsed.format('YYYY-MM-DD')
  const time = parsed.format('HH:mm:ss')
  const isBegin = time === BEGIN_DEFAULT_TIME
  const isExpected = time === EXPECTED_DEFAULT_TIME

  return {
    date,
    time: isBegin || isExpected ? null : parsed.format('HH:mm'),
    hasCustomTime: !isBegin && !isExpected,
  }
}

export function buildTaskDateTime(
  date: string | null | undefined,
  time: string | null | undefined,
  field: TaskDateField
): string | null {
  if (!date || date.trim() === '') {
    return null
  }

  const defaultTime = field === 'expected' ? EXPECTED_DEFAULT_TIME : BEGIN_DEFAULT_TIME
  const normalizedTime = time && time.trim() !== '' ? `${time.trim()}:00`.slice(0, 8) : defaultTime

  return `${date.trim()} ${normalizedTime.length === 5 ? `${normalizedTime}:00` : normalizedTime}`
}

export function formatTaskDateTimeDisplay(value?: string | null, options?: {withTime?: boolean}): string {
  if (!value || value.trim() === '') {
    return ''
  }

  const parsed = parseTaskDateTime(value)
  if (!parsed.date) {
    return ''
  }

  const dateLabel = dayjs(parsed.date).format('DD/MM/YYYY')
  if (options?.withTime === false || !parsed.hasCustomTime || !parsed.time) {
    return dateLabel
  }

  return `${dateLabel} ${parsed.time}`
}

export function toDatePickerValue(value?: string | null): Date | null {
  const parsed = parseTaskDateTime(value)
  if (!parsed.date) {
    return null
  }

  const date = new Date(`${parsed.date}T00:00:00`)
  return Number.isNaN(date.getTime()) ? null : date
}

export function isValidTaskDateTimeRange(
  beginDateTime?: string | null,
  expectedDateTime?: string | null
): boolean {
  if (!beginDateTime || !expectedDateTime) {
    return true
  }

  const begin = dayjs(beginDateTime, [TASK_DATETIME_FORMAT, 'YYYY-MM-DD'], true)
  const expected = dayjs(expectedDateTime, [TASK_DATETIME_FORMAT, 'YYYY-MM-DD'], true)

  if (!begin.isValid() || !expected.isValid()) {
    return true
  }

  return begin.isBefore(expected) || begin.isSame(expected)
}

export function normalizeIncomingTaskDateTime(value?: string | null, field: TaskDateField = 'expected'): string | null {
  if (!value || value.trim() === '') {
    return null
  }

  if (DATETIME_REGEX.test(value.trim()) || DATE_ONLY_REGEX.test(value.trim())) {
    return buildTaskDateTime(
      DATE_ONLY_REGEX.test(value.trim()) ? value.trim() : value.trim().slice(0, 10),
      DATE_ONLY_REGEX.test(value.trim()) ? null : value.trim().slice(11, 16),
      field
    )
  }

  const parsed = dayjs(value)
  if (!parsed.isValid()) {
    return null
  }

  return parsed.format(TASK_DATETIME_FORMAT)
}
